import {
  ClipboardList,
  ArrowRightCircle,
  MapPin,
  CreditCard,
  CheckSquare,
  Wallet,
  Mail,
  PackageCheck,
} from "lucide-react";

/**
 * BBK Checkout Flow — an 8-step serpentine flow chart on the public landing.
 * Desktop: 4-column grid, row 1 flows left→right, row 2 flows right→left (serpentine).
 * Mobile: single column, arrows pointing down between steps.
 *
 * Sits directly below the "Domestic. Direct. Answered by a human." section.
 */

const STEPS = [
  { key: "create",   n: "01", title: "Create Order",   sub: "Choose your kits & sizes.",     icon: ClipboardList },
  { key: "proceed",  n: "02", title: "Proceed to Checkout", sub: "Review cart & totals.",   icon: ArrowRightCircle },
  { key: "ship",     n: "03", title: "Shipping Address", sub: "Confirm domestic address.",  icon: MapPin },
  { key: "pay-pick", n: "04", title: "Payment Choice", sub: "Card, Venmo, or Cash App.",   icon: CreditCard },
  { key: "place",    n: "05", title: "Place Order",     sub: "Lock in your kits.",           icon: CheckSquare },
  { key: "pay-do",   n: "06", title: "Make Payment",    sub: "Complete the transaction.",   icon: Wallet },
  { key: "tracking", n: "07", title: "Tracking via Email", sub: "Live shipment updates.",  icon: Mail },
  { key: "received", n: "08", title: "Shipment Received", sub: "Kits arrive at your door.", icon: PackageCheck },
];

export default function LandingFlowSection() {
  return (
    <section
      className="relative bg-white border-t border-borderSubtle py-20 sm:py-28 lg:py-32"
      data-testid="landing-flow"
      aria-label="BBK Checkout Flow"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl">
          <p className="text-[13px] uppercase tracking-[0.32em] text-oxblood font-mono">
            Order lifecycle
          </p>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal tracking-tight leading-[1.02]">
            The BBK <span className="italic text-charcoal/85">checkout flow.</span>
          </h2>
          <p className="mt-5 max-w-[52ch] text-base text-stone leading-relaxed">
            Eight straightforward steps from cart to doorstep. No surprises,
            no hidden fees, no waiting weeks on unclear tracking.
          </p>
        </div>

        {/* Desktop: 4-col two-row grid · Mobile: 1-col stack.
            Both rows flow left→right so step numbers stay in reading order. */}
        <div className="mt-16 sm:mt-20">
          <FlowRow steps={STEPS.slice(0, 4)} />
          <FlowRow steps={STEPS.slice(4)} className="mt-6 sm:mt-10" />
        </div>
      </div>
    </section>
  );
}

function FlowRow({ steps, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
        {steps.map((s, idx) => (
          <div key={s.key} className="relative">
            <FlowCard step={s} />

            {/* Between-card connector (desktop only) — right-facing arrow */}
            {idx < steps.length - 1 && (
              <FlowArrow pointing="right" className="hidden lg:flex" />
            )}

            {/* Between-card connector (mobile only) — downward arrow */}
            <FlowArrow
              pointing="down"
              className="lg:hidden mx-auto mt-4 mb-1"
              mobile
              hidden={idx === steps.length - 1}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function FlowCard({ step }) {
  const Icon = step.icon;
  return (
    <div
      className="relative bg-bone border border-borderSubtle rounded-[6px] p-5 sm:p-6 h-full flex flex-col transition-all duration-500 hover:border-oxblood/40 hover:bg-white"
      data-testid="landing-flow-card"
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-[12px] font-mono tracking-[0.24em] text-stone">
          {step.n}
        </span>
        <div
          className="w-9 h-9 rounded-[5px] grid place-items-center shrink-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(74,14,23,0.08), rgba(140,123,102,0.06))",
          }}
          aria-hidden
        >
          <Icon size={16} strokeWidth={1.5} className="text-oxblood" />
        </div>
      </div>

      <h3 className="mt-5 font-serif text-lg sm:text-xl text-charcoal tracking-tight leading-[1.1]">
        {step.title}
      </h3>
      <p className="mt-2 text-xs sm:text-[13px] text-stone leading-relaxed">
        {step.sub}
      </p>
    </div>
  );
}

function FlowArrow({ pointing = "right", className = "", mobile = false, hidden = false }) {
  if (hidden) return null;
  // "right", "left", or "down"
  const size = mobile ? 20 : 18;
  const rotation =
    pointing === "left" ? 180 : pointing === "down" ? 90 : 0;

  // Desktop arrows sit absolutely on the right edge, centered vertically.
  const positionCls = mobile
    ? "flex items-center justify-center"
    : "absolute top-1/2 -right-3 -translate-y-1/2 z-10 items-center";

  return (
    <span
      className={`${positionCls} ${className}`}
      style={{ transform: mobile ? undefined : "translateY(-50%)" }}
      aria-hidden
    >
      <svg width={size} height={size} viewBox="0 0 20 20" style={{ transform: `rotate(${rotation}deg)` }}>
        <path d="M2 10 H16" stroke="#4A0E17" strokeWidth="1.25" strokeLinecap="round" />
        <path d="M11 5 L16 10 L11 15" stroke="#4A0E17" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </span>
  );
}
